import {
	CurrencyEnum,
	useCreateOrganizationMutation,
	useGetAllLocalizationCountriesQuery,
	useGetLocalizationStatesQuery,
	useValidateCvrLazyQuery,
	useGetPlaceDetailsLazyQuery,
	useGenerateVatFromRegistrationLazyQuery,
	useGetAllOrganizationIndustriesQuery,
	useEconomicPaymentTermsQuery,
} from '@codezo/graphql';
import { AlertCircle, Check, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { AddressAutocomplete } from '../../components/ui/address-autocomplete';
import { AnimatedHeight } from '../../components/ui/animated-height';
import { Field } from '../../components/ui/field';
import { Input } from '../../components/ui/input';
import { SelectPopover } from '../../components/ui/select-popover';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { CVR_LOOKUP_ENABLED, validateClientDetails, validateCvrFormat } from './client-validation';

/* ════════════════════════ ADD CLIENT MODAL ════════════════════════ */

export function AddClientModal({
	open,
	onOpenChange,
	onSuccess,
	initialName,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Fired with the new organization's id + name so callers can preselect it. */
	onSuccess?: (id?: string, name?: string) => void;
	/** Prefill for the client name field (e.g. search text that had no match). */
	initialName?: string;
}) {
	// Form state
	const [name, setName] = useState('');
	const [countryCode, setCountryCode] = useState('');
	const [websiteUrl, setWebsiteUrl] = useState('');
	const [industryId, setIndustryId] = useState('');
	const [address, setAddress] = useState({
		addressLine1: '',
		addressLine2: '',
		city: '',
		stateCode: '',
		postalCode: '',
		countryCode: '',
		googlePlaceId: '',
		latitude: null as number | null,
		longitude: null as number | null,
	});
	const [businessRegistrations, setBusinessRegistrations] = useState<Record<string, string>>({});
	const [paymentTermsNumber, setPaymentTermsNumber] = useState('');

	// Prefill the name when opened from a search that had no matching client.
	useEffect(() => {
		if (open && initialName) {
			setName((prev) => prev || initialName);
		}
	}, [open, initialName]);

	// CVR validation
	const [cvrValidating, setCvrValidating] = useState(false);
	const [cvrValidated, setCvrValidated] = useState<boolean | null>(null);
	const [cvrError, setCvrError] = useState<string | null>(null);
	const [lastValidatedCvr, setLastValidatedCvr] = useState<string | null>(null);

	// Queries
	const { data: countriesData } = useGetAllLocalizationCountriesQuery({
		fetchPolicy: 'no-cache',
		skip: !open,
	});
	const { data: industryData } = useGetAllOrganizationIndustriesQuery({
		fetchPolicy: 'no-cache',
		skip: !open,
	});
	const { data: paymentTermsData, error: paymentTermsError } = useEconomicPaymentTermsQuery({
		fetchPolicy: 'no-cache',
		skip: !open,
	});

	const selectedCountry = useMemo(() => {
		if (!countriesData?.getAllLocalizationCountries || !countryCode) return null;
		return countriesData.getAllLocalizationCountries.find((c) => c.code === countryCode);
	}, [countriesData, countryCode]);

	const { data: statesData } = useGetLocalizationStatesQuery({
		variables: { countryCode },
		skip: !countryCode || !selectedCountry?.hasStates,
		fetchPolicy: 'no-cache',
	});

	const countryOptions = useMemo(() => {
		if (!countriesData?.getAllLocalizationCountries) return [];
		return countriesData.getAllLocalizationCountries.map((c) => ({
			value: c.code,
			label: c.name,
		}));
	}, [countriesData]);

	const stateOptions = useMemo(() => {
		if (!statesData?.getLocalizationStates) return [];
		return statesData.getLocalizationStates.map((s) => ({
			value: s.code,
			label: s.name,
		}));
	}, [statesData]);

	const industryOptions = useMemo(
		() =>
			industryData?.getAllOrganizationIndustries?.map((i) => ({
				value: i.id,
				label: i.name,
			})) ?? [],
		[industryData]
	);

	const paymentTermsOptions = useMemo(
		() =>
			paymentTermsData?.economicPaymentTerms?.map((t) => ({
				value: String(t.paymentTermsNumber),
				label: t.daysOfCredit != null ? `${t.name} (${t.daysOfCredit} days)` : t.name,
			})) ?? [],
		[paymentTermsData]
	);

	const selectedPaymentTerm = useMemo(
		() =>
			paymentTermsData?.economicPaymentTerms?.find(
				(t) => String(t.paymentTermsNumber) === paymentTermsNumber
			) ?? null,
		[paymentTermsData, paymentTermsNumber]
	);

	// e-conomic returns a specific message when the integration isn't set up.
	const paymentTermsUnavailable = Boolean(paymentTermsError);

	// Prefill with the backend-flagged default once, without clobbering a manual pick.
	useEffect(() => {
		if (paymentTermsNumber) return;
		const terms = paymentTermsData?.economicPaymentTerms;
		if (!terms?.length) return;
		const fallback = terms.find((t) => t.isDefault) ?? terms[0];
		setPaymentTermsNumber(String(fallback.paymentTermsNumber));
	}, [paymentTermsData, paymentTermsNumber]);

	// Lazy queries
	const [getPlaceDetails] = useGetPlaceDetailsLazyQuery();
	const [validateCvr] = useValidateCvrLazyQuery();
	const [generateVatFromRegistration] = useGenerateVatFromRegistrationLazyQuery();
	const [createOrganization, { loading }] = useCreateOrganizationMutation();

	// Address selection
	const [sessionToken] = useState(() => crypto.randomUUID());

	// The country is chosen up front and stays authoritative — an address pick
	// only fills the address block, it never re-decides the country (and so never
	// wipes the business registrations the user already typed).
	const handleAddressSelect = useCallback(
		async (placeId: string) => {
			const { data } = await getPlaceDetails({ variables: { placeId, sessionToken } });
			if (!data?.getPlaceDetails) return;

			const d = data.getPlaceDetails;
			const line1 = [d.streetNumber, d.streetName].filter(Boolean).join(' ');

			setAddress({
				addressLine1: line1 || d.formattedAddress || '',
				addressLine2: '',
				city: d.city || '',
				stateCode: d.stateCode || '',
				postalCode: d.postalCode || '',
				countryCode: d.countryCode || countryCode,
				googlePlaceId: d.placeId || '',
				latitude: d.latitude ?? null,
				longitude: d.longitude ?? null,
			});
		},
		[getPlaceDetails, sessionToken, countryCode]
	);

	// CVR validation
	const cvrRegistration = selectedCountry?.registrations?.find((r) => r.code === 'CVR');
	const cvrNumber = useDebouncedValue(
		cvrRegistration ? businessRegistrations.CVR || '' : '',
		500
	);

	useEffect(() => {
		if (cvrNumber === lastValidatedCvr) return;

		const hasCvr = selectedCountry?.registrations?.some((r) => r.code === 'CVR');
		if (!hasCvr || !cvrNumber) {
			setCvrValidated(null);
			setCvrError(null);
			setLastValidatedCvr(null);
			return;
		}

		// Empty is already handled above, so any format error here is a real one.
		const formatError = validateCvrFormat(cvrNumber);
		if (formatError) {
			setCvrError(formatError);
			setCvrValidated(false);
			setLastValidatedCvr(cvrNumber);
			return;
		}

		// Well-formed — validate against the registry
		if (!CVR_LOOKUP_ENABLED) {
			// Registry lookup disabled — accept the format, skip the call.
			// No autofill; name and address are entered manually.
			setCvrValidating(false);
			setCvrError(null);
			setCvrValidated(true);
			setLastValidatedCvr(cvrNumber);
			setBusinessRegistrations((prev) => ({
				...prev,
				CVR: cvrNumber,
				VAT: prev.VAT || `DK${cvrNumber}`,
			}));
			return;
		}

		setCvrValidating(true);
		setCvrError(null);
		setLastValidatedCvr(cvrNumber);

		validateCvr({ variables: { cvrNumber } })
			.then(({ data }) => {
				if (data?.validateCVR?.valid && data.validateCVR.companyInfo) {
					const info = data.validateCVR.companyInfo;
					// Registry hit — fill the fields the admin would otherwise type.
					setName(info.name);
					setAddress((prev) => ({
						...prev,
						addressLine1: info.address?.street || prev.addressLine1,
						city: info.address?.city || prev.city,
						postalCode: info.address?.zipcode || prev.postalCode,
						countryCode: 'DK',
					}));
					setBusinessRegistrations((prev) => ({
						...prev,
						CVR: cvrNumber,
						VAT: info.vat ? `DK${info.vat}` : prev.VAT || '',
					}));
					setCvrValidated(true);
				} else {
					setCvrError(data?.validateCVR?.error || 'CVR number not found');
					setCvrValidated(false);
				}
			})
			.catch(() => {
				setCvrError('Failed to validate CVR');
				setCvrValidated(false);
			})
			.finally(() => setCvrValidating(false));
	}, [cvrNumber, selectedCountry, validateCvr, lastValidatedCvr]);

	// VAT generation for non-CVR registrations
	const handleRegistrationChange = useCallback(
		async (code: string, value: string) => {
			setBusinessRegistrations((prev) => ({ ...prev, [code]: value }));

			const reg = selectedCountry?.registrations?.find((r) => r.code === code);
			if (reg?.vatDerivationPattern && value && countryCode) {
				try {
					const { data } = await generateVatFromRegistration({
						variables: {
							countryCode,
							registrationNumber: value,
							registrationType: code,
						},
					});
					if (data?.generateVATFromRegistration) {
						setBusinessRegistrations((prev) => ({
							...prev,
							VAT: data.generateVATFromRegistration!,
						}));
					}
				} catch (err) {
					// Silently fail
				}
			}
		},
		[selectedCountry, countryCode, generateVatFromRegistration]
	);

	// Reset
	const resetForm = () => {
		setName('');
		setCountryCode('');
		setWebsiteUrl('');
		setIndustryId('');
		setAddress({
			addressLine1: '',
			addressLine2: '',
			city: '',
			stateCode: '',
			postalCode: '',
			countryCode: '',
			googlePlaceId: '',
			latitude: null,
			longitude: null,
		});
		setBusinessRegistrations({});
		setPaymentTermsNumber('');
		setCvrValidated(null);
		setCvrError(null);
		setLastValidatedCvr(null);
	};

	// Submit
	const handleSubmit = async () => {
		if (!name.trim() || !countryCode) return;

		// economicPaymentTermsNumber is required by the backend (Int!).
		if (!paymentTermsNumber) {
			toast.error('Select payment terms');
			return;
		}

		const regs = Object.entries(businessRegistrations)
			.filter(([, v]) => v)
			.map(([type, number]) => ({ type, number }));

		try {
			const { data } = await createOrganization({
				variables: {
					input: {
						name: name.trim(),
						countryCode,
						organizationIndustryId: industryId || undefined,
						websiteUrl: websiteUrl.trim() || undefined,
						currency: selectedCountry?.currency
							? (selectedCountry.currency as CurrencyEnum)
							: undefined,
						economicPaymentTermsNumber: Number(paymentTermsNumber),
						economicPaymentTermsName: selectedPaymentTerm?.name,
						businessRegistrations: regs.length > 0 ? regs : undefined,
						address: address.addressLine1
							? {
									addressLine1: address.addressLine1,
									addressLine2: address.addressLine2 || undefined,
									city: address.city,
									stateCode: address.stateCode || undefined,
									postalCode: address.postalCode,
									countryCode: address.countryCode,
									googlePlaceId: address.googlePlaceId || undefined,
									latitude: address.latitude,
									longitude: address.longitude,
								}
							: undefined,
					},
				},
			});

			if (data?.createOrganization?.success) {
				toast.success('Client created successfully');
				resetForm();
				onOpenChange(false);
				onSuccess?.(data.createOrganization.referenceKey ?? undefined, name.trim());
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to create client');
		}
	};

	/* Payment terms are create-only (they live under Settings once the client
	   exists), so they sit alongside the rules shared with the edit surface. */
	const canSubmit =
		Object.keys(validateClientDetails({ name, countryCode })).length === 0 &&
		paymentTermsNumber.length > 0;

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next) resetForm();
				onOpenChange(next);
			}}
		>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>Add client</DialogTitle>
					<DialogDescription>
						Start with the client’s country — it decides which business registration
						numbers apply.
					</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						{/* No overflow-hidden: the address autocomplete drops a non-portaled
					    suggestion list, which a clipped container would cut off. */}
						<AnimatedHeight
							step={countryCode || 'no-country'}
							className="transition-[height] duration-200 ease-out"
						>
							<div className="flex flex-col gap-5">
								{/* ── Step 1: Country ── */}
								<Field label="Country" required>
									<SelectPopover
										value={countryCode}
										onChange={(v) => {
											setCountryCode(v);
											setAddress((p) => ({ ...p, countryCode: v }));
											setBusinessRegistrations({});
											setCvrValidated(null);
											setCvrError(null);
											setLastValidatedCvr(null);
										}}
										options={countryOptions}
										placeholder="Select country"
									/>
								</Field>

								{!countryCode && (
									<p className="text-xs text-text-4">
										Select a country to continue.
									</p>
								)}

								{countryCode && (
									<>
										{/* ── Step 2: Business registrations ── */}
										{selectedCountry?.registrations &&
											selectedCountry.registrations.length > 0 && (
												<>
													<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
														{selectedCountry.registrations.map(
															(reg) => (
																<Field
																	key={reg.code}
																	label={reg.label}
																	required={reg.isRequired}
																	error={
																		reg.code === 'CVR'
																			? cvrError
																			: null
																	}
																>
																	<div className="relative">
																		<Input
																			value={
																				businessRegistrations[
																					reg.code
																				] || ''
																			}
																			onChange={(e) =>
																				handleRegistrationChange(
																					reg.code,
																					e.target.value
																				)
																			}
																			placeholder={
																				reg.placeholder ||
																				undefined
																			}
																		/>
																		{reg.code === 'CVR' && (
																			<div className="absolute right-2.5 top-1/2 -translate-y-1/2">
																				{cvrValidating && (
																					<Loader2
																						size={14}
																						className="animate-spin text-text-4"
																					/>
																				)}
																				{CVR_LOOKUP_ENABLED &&
																					cvrValidated ===
																						true &&
																					!cvrValidating && (
																						<Check
																							size={
																								11
																							}
																							className="text-[var(--color-status-success)]"
																						/>
																					)}
																				{cvrValidated ===
																					false &&
																					!cvrValidating && (
																						<AlertCircle
																							size={
																								11
																							}
																							className="text-destructive"
																						/>
																					)}
																			</div>
																		)}
																	</div>
																</Field>
															)
														)}
													</div>
													{cvrRegistration && (
														<p className="text-xs text-text-4">
															{CVR_LOOKUP_ENABLED
																? 'Enter the CVR number and we’ll fill in the company name and address automatically.'
																: 'CVR lookup is temporarily unavailable — enter the company details below manually.'}
														</p>
													)}
												</>
											)}

										{/* ── Divider ── */}
										<div className="border-t border-line-1" />

										{/* ── Step 3: Organization details ── */}
										<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
											<Field label="Client name" required>
												<Input
													value={name}
													onChange={(e) => setName(e.target.value)}
													placeholder="e.g. Acme Corporation"
												/>
											</Field>
											<Field label="Website">
												<Input
													value={websiteUrl}
													onChange={(e) => setWebsiteUrl(e.target.value)}
													placeholder="https://example.com"
												/>
											</Field>
										</div>

										<Field label="Industry">
											<SelectPopover
												value={industryId}
												onChange={setIndustryId}
												options={industryOptions}
												placeholder="Select industry"
											/>
										</Field>

										{/* ── Divider ── */}
										<div className="border-t border-line-1" />

										{/* ── Step 4: Address ── */}
										<Field label="Search address">
											<AddressAutocomplete
												onSelect={handleAddressSelect}
												countryCode={countryCode}
											/>
										</Field>

										<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
											<Field label="Address line 1">
												<Input
													value={address.addressLine1}
													onChange={(e) =>
														setAddress((p) => ({
															...p,
															addressLine1: e.target.value,
														}))
													}
													placeholder="Street address"
												/>
											</Field>
											<Field label="Address line 2">
												<Input
													value={address.addressLine2}
													onChange={(e) =>
														setAddress((p) => ({
															...p,
															addressLine2: e.target.value,
														}))
													}
													placeholder="Apt, suite, etc."
												/>
											</Field>
											<Field label="City">
												<Input
													value={address.city}
													onChange={(e) =>
														setAddress((p) => ({
															...p,
															city: e.target.value,
														}))
													}
													placeholder="City"
												/>
											</Field>
											{selectedCountry?.hasStates && (
												<Field
													label={
														selectedCountry.addressFormat?.stateLabel ||
														'State'
													}
												>
													<SelectPopover
														value={address.stateCode}
														onChange={(v) =>
															setAddress((p) => ({
																...p,
																stateCode: v,
															}))
														}
														options={stateOptions}
														placeholder="Select state"
													/>
												</Field>
											)}
											<Field
												label={
													selectedCountry?.addressFormat
														?.postalCodeLabel || 'Postal code'
												}
											>
												<Input
													value={address.postalCode}
													onChange={(e) =>
														setAddress((p) => ({
															...p,
															postalCode: e.target.value,
														}))
													}
													placeholder="Postal code"
												/>
											</Field>
										</div>

										{/* ── Divider ── */}
										<div className="border-t border-line-1" />

										{/* ── Step 5: Billing ── */}
										<Field
											label="Payment terms"
											required
											error={
												paymentTermsUnavailable
													? 'e-conomic integration is not configured'
													: null
											}
										>
											<SelectPopover
												value={paymentTermsNumber}
												onChange={setPaymentTermsNumber}
												options={paymentTermsOptions}
												placeholder={
													paymentTermsUnavailable
														? 'Unavailable'
														: 'Select payment terms'
												}
											/>
										</Field>
									</>
								)}
							</div>
						</AnimatedHeight>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => {
								resetForm();
								onOpenChange(false);
							}}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!canSubmit || loading}>
							{loading ? 'Creating…' : 'Create client'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
